import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { purchaseOrderSendDefaultMessage } from '@/features/purchase-order/config/purchase-order-send-default-message';
import { PurchaseOrderSendAttachmentPreview } from '@/features/purchase-order/purchase-order-send-attachment-preview';
import { PurchaseOrderSendRecipients } from '@/features/purchase-order/purchase-order-send-recipients';
import type { Warehouse } from '@/types/core/warehouse';

type PurchaseOrderSendDialogProps = {
  purchaseOrderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (
    params: { recipients: string[]; message: string; warehouseId: number },
    onSuccess?: () => void,
  ) => void;
  isSending: boolean;
  initialRecipientEmails: string[];
  initialRecipientLabels: Record<string, string>;
  availableWarehouses: Warehouse[];
  selectedWarehouseId: number;
  sendingBlockedReason: string | null;
};

export function PurchaseOrderSendDialog({
  purchaseOrderId,
  open,
  onOpenChange,
  onSend,
  isSending,
  initialRecipientEmails,
  initialRecipientLabels,
  availableWarehouses,
  selectedWarehouseId,
  sendingBlockedReason,
}: PurchaseOrderSendDialogProps) {
  const { t } = useTranslation('purchaseOrder');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [message, setMessage] = useState(purchaseOrderSendDefaultMessage);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setRecipients(
        initialRecipientEmails.length > 0 ? [...initialRecipientEmails] : [],
      );
      setMessage(purchaseOrderSendDefaultMessage);
      setWarehouseId(`${selectedWarehouseId}`);
    }
    wasOpenRef.current = open;
  }, [open, initialRecipientEmails, selectedWarehouseId]);

  const handleSend = () => {
    if (!warehouseId || sendingBlockedReason || isSending) {
      return;
    }

    onSend(
      {
        recipients,
        message,
        warehouseId: Number(warehouseId),
      },
      () => onOpenChange(false),
    );
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSending) {
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Enter' &&
      !(event.target instanceof HTMLTextAreaElement)
    ) {
      event.preventDefault();
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className='sm:max-w-3xl' onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>{t('sendPo')}</DialogTitle>
          <DialogDescription />
        </DialogHeader>
        <div className='flex flex-col gap-4'>
          <PurchaseOrderSendRecipients
            isSending={isSending}
            onChange={setRecipients}
            open={open}
            recipientLabels={initialRecipientLabels}
            recipients={recipients}
          />
          <div className='flex flex-col gap-2'>
            <Label htmlFor='message'>{t('sendPoModalMessage')}</Label>
            <Textarea
              className='field-sizing-fixed max-h-60 min-h-24 overflow-y-auto'
              id='message'
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              value={message}
            />
          </div>
          <div className='flex flex-col gap-2'>
            <Label htmlFor='warehouse'>{t('sendPoModalWarehouse')}</Label>
            <Select
              disabled={isSending}
              onValueChange={setWarehouseId}
              value={warehouseId}
            >
              <SelectTrigger className='w-full' id='warehouse'>
                <SelectValue
                  placeholder={t('sendPoModalWarehousePlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                {availableWarehouses.map((warehouse) => (
                  <SelectItem key={warehouse.id} value={`${warehouse.id}`}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <PurchaseOrderSendAttachmentPreview
            isSending={isSending}
            purchaseOrderId={purchaseOrderId}
          />
        </div>
        <DialogFooter>
          {sendingBlockedReason ? (
            <p className='text-destructive text-sm' role='status'>
              {sendingBlockedReason}
            </p>
          ) : null}
          <DialogClose asChild>
            <Button disabled={isSending} type='button' variant='outline'>
              {t('cancel', { ns: 'common' })}
            </Button>
          </DialogClose>
          <Button
            disabled={isSending || Boolean(sendingBlockedReason)}
            onClick={handleSend}
            type='button'
          >
            {t('sendPoModalSend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
